import React, { useEffect, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import AddTrainingModulesBtn from '../UpdateContents/AddTrainingModules/AddTrainingModulesBtn';
import TrainingModulesModal from '../UpdateContents/AddTrainingModules/TrainingModulesModal';
import { useGetLearningPathQuery } from '../../LearningPaths.queries';
import styles from './LearningPathDetails.module.scss';
import ListOfContents from './ListOfContents';
import { useLearningPathsStore } from '../../LearningPaths.store';
import UserAvatars from './UserAvatars';
import StatusLabel from 'shared_elements/StatusLabel/StatusLabel';
import { PATH_STATUS, usePathValidations } from '../../LearningPaths.utils';
import ChangeStatusButton from './ChangeStatusButton';
import { imageBasePath } from 'constants';
import DateTime from 'infrastructure/Datetime';
import EditLearningPath from '../EditLearningPath/EditLearningPath';
import User from 'infrastructure/User';
import Organization from 'infrastructure/Organization';
import { useSalesRepView } from 'features/salesRepView/SalesRepView.hook';
import { useGetFF } from '@fstack-tech/ff_react_module';

const LearningPathDetails = () => {
  const location = useLocation();
  const history = useHistory();
  const { isEnabled: ff_learningPaths, loading: isLoadingLearningPaths } = useGetFF({
    flag_id: 'learning_paths',
  });
  const { isEnabled: ff_edit_users_skill_paths, loading: isLoadingEdit } = useGetFF({
    flag_id: 'edit_users_skill_paths',
  });
  const { isActive, isLoading } = useSalesRepView();
  const authorized = Organization.isRep() ? false : !isActive;

  useEffect(() => {
    if (!authorized) history.push('/training');
  }, [authorized, history]);

  const uuid = useMemo(() => location?.pathname?.replace('/learning-path/', ''), [location]);
  if (!uuid) history.push('/training?active=learningPaths');

  const { data } = useGetLearningPathQuery({ uuid, enabled: !!uuid && authorized && !isLoading });
  const { isPathOwner } = usePathValidations({ learningPath: data });
  const { setContents, setLearningPath, resetLearningPath, setCurrentLearningPath, openEditModal } =
    useLearningPathsStore();

  const usersList = useMemo(
    () =>
      data?.users.map((user) => {
        return { label: User.getUserName(user), value: user.uuid };
      }),
    [data]
  );
  useEffect(() => {
    if (!data) return;
    setContents(data?.contents || []);
    setLearningPath(data);
    setCurrentLearningPath({
      deadline: DateTime.formatDate(new Date(data?.deadline)) + ' 00:00:00',
      users: usersList,
      title: data?.title,
      description: data?.description,
      uuid: data?.uuid,
    });
  }, [data, setContents, setLearningPath, setCurrentLearningPath, usersList]);

  useEffect(() => {
    return function clean() {
      setContents([]);
      resetLearningPath();
    };
  }, [setContents, resetLearningPath]);

  const handleBack = () => {
    history.push('/training?active=learningPaths');
  };

  if (isLoadingLearningPaths || isLoadingEdit)
    return (
      <div className={styles.contentContainer}>
        <div
          style={{
            height: '40vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          Loading ....
        </div>
      </div>
    );
  return (
    <div>
      <EditLearningPath
        isPathOwner={isPathOwner}
        ff_edit_users_skill_paths={ff_edit_users_skill_paths}
      />
      <div className={styles.infoContainer}>
        <div className={styles.titleContainer}>
          <div className={styles.titleContainerText}>
            <button onClick={handleBack} className={styles.backBtn}>
              <img src={imageBasePath + 'back_arrow.svg'} alt="img" />
              <span>Go Back</span>
            </button>
            {!ff_learningPaths && (
              <div>
                <div className={styles.title}>
                  {data?.title || ''}{' '}
                  <StatusLabel
                    published={data?.status === PATH_STATUS.PUBLISH}
                    draft={data?.status === PATH_STATUS.DRAFT}
                  />
                </div>
                <p className={styles.description}>{data?.description || ''}</p>
              </div>
            )}
          </div>
        </div>

        <div className={styles.buttons}>
          {!ff_learningPaths && <UserAvatars users={data?.users} />}
          {(isPathOwner || ff_edit_users_skill_paths) && (
            <span className={styles.buttonsEdit} onClick={openEditModal}>
              Edit details
            </span>
          )}
          <ChangeStatusButton />
        </div>
      </div>
      <div className={styles.contentContainer}>
        {ff_learningPaths ? (
          <div className={styles.focusContentContainer}>
            <AddTrainingModulesBtn />
            <ListOfContents />
            <TrainingModulesModal />
          </div>
        ) : (
          <>
            <AddTrainingModulesBtn />
            <ListOfContents />
            <TrainingModulesModal />
          </>
        )}
      </div>
    </div>
  );
};

export default LearningPathDetails;
